/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'./AbstractTupleLocator'
], function (_, AbstractTupleLocator) {
	class BinarySearchTupleLocator extends AbstractTupleLocator {

		/**
		 * A tuple locator which deals with tuples which the VIDA getItemsInPolygon
		 * API returns.
		 */
		constructor({ viprWidget }) {
			super();
			this.viprWidget = viprWidget;
		}

		/* For JSDocs, see parent class */
		findTupleVisualInfo(filter, { includeInfo, includePoints }) {
			const viprItems = _.flatten(
				//Get all items in an area
				this._getVisAreas().map(({ x, y, w, h }) =>
					this._getVIDAItemsInRectangle({ x0: x, y0: y, x1: x+w, y1: y+h })
					//Filter items to ones which satisfy specified filter
						.filter(item => this._itemSatisfiesFilter({ item, filter }))
				)
			);

			return viprItems.map(item => {
				const result = {};
				if(includeInfo) {result.info = this._getValueInfoFromItem(item);}
				if(includePoints) {result.point = this._getPoint(item);}
				return result;
			});
		}

		/*
		 * Finds the areas (e.g. "visualization", "legend") and their extents which
		 * VIPR partitions the entire visualization into.
		 *
		 * Motivation: It is important to know their extents, as if a
		 * getItemsInPolygon query draws a polygon with points straddling multiple
		 * areas, VIPR will return no items.
		 *
		 * @return {object[]} area information will include 'area' (name), 'x', 'y',
		 * 'w' and 'h'
		 */
		_getVisAreas() {
			const bounds = this.viprWidget.domNode.getBoundingClientRect();

			const areaToExtents = {};

			//Assume all areas we care about begin in one of the four corners of the
			//visualization.
			const findAreasFromCorner = (bx,by) => {
				const w0 = bx ? bounds.width - 1 : 0,
					h0 = by ? bounds.height - 1 : 0;

				const getArea = (w, h) => this.viprWidget.getVisCoordinate(bounds.x + w, bounds.y + h).area;

				//What area is in this corner?
				const area = getArea(w0, h0);

				//'none' is not a useful area; and if we've already found the extents of
				//this area, no need to scan again
				if(area === 'none' || areaToExtents[area]) {
					return;
				}

				const dx = bx ? -1 : 1,
					dy = by ? -1 : 1;
				let wt, ht;

				//Scan x axis to see how wide this area is
				for(wt = w0; 0 <= wt && wt < bounds.width; wt+=dx) {
					if(getArea(wt, h0) !== area) {
						break;
					}
				}
				wt -= dx;

				//Scan y axis to see how tall this area is
				for(ht = h0; 0 <= ht && ht < bounds.height; ht+=dy) {
					if(getArea(w0, ht) !== area) {
						break;
					}
				}
				ht -= dy;

				//Assume areas are rectangular
				areaToExtents[area] =  {
					x: Math.min(w0, wt),
					y: Math.min(h0, ht),
					w: Math.abs(w0 - wt),
					h: Math.abs(h0 - ht)
				};
			};

			//Search for areas in all four corners
			findAreasFromCorner(false, false); //top-left
			findAreasFromCorner(true, false);  //top-right
			findAreasFromCorner(false, true);  //bottom-left
			findAreasFromCorner(true, true);   //bottom-right

			//In general, the only area we use is 'visualization'. If it's not found
			//we won't be able to proceed
			if(!areaToExtents.visualization) {
				throw new Error('Cannot find "visualization" area of widget');
			}

			return Object.keys(areaToExtents)
				.map(area => ({ extents: areaToExtents[area], area }) )
				.map(({ extents, area }) => ({ ...extents, area }));
		}

		/*
		 * Simplifies the VIPR getItemsInPolygon API to a rectangle - and takes care
		 * of the mapping to VIPR coordinate space via getVisCoordinate.
		 * @param {object} coords
		 * @param {number} coords.x0 - top-left corner x coordinate
		 * @param {number} coords.y0 - top-left corner y coordinate
		 * @param {number} coords.x1 - bottom-right corner x coordinate
		 * @param {number} coords.y1 - bottom-right corner y coordinate
		 */
		_getVIDAItemsInRectangle({ x0, y0, x1, y1 }) {
			const origin = this.viprWidget.domNode.getBoundingClientRect();

			//Make a 'rectangle' polygon:
			//	- top-left corner
			//	- top-right corner
			//	- bottom-right corner
			//	- bottom-left corner
			const points = [[x0, y0],[x1, y0],[x1, y1],[x0, y1]]
				.map(([x,y]) => ([x + origin.x, y + origin.y]))
				.map(([x,y]) => (this.viprWidget.getVisCoordinate(x, y)));

			return this.viprWidget.getItemsInPolygon(points);
		}

		/*
		 * Finds a single clickable point which will click an item
		 * @param {object} item
		 */
		_getPoint(item) {
			//Find which area this item is contained in and extract its extents
			const extent = this._getVisAreas().find(
				({ x,y,w,h }) => this._isItemContainedIn(item, { x0: x, y0: y, x1: x+w, y1: y+h })
			);

			if(!extent) {
				throw new Error('Item not found at any position within visualization');
			}

			const x0 = extent.x,
				y0 = extent.y,
				x1 = x0 + extent.w,
				y1 = y0 + extent.h;

			//Find the boundary between where this item does and does not exist in the x axis
			const x = this._binarySearch(x1, x0, x => this._isItemContainedIn(item, { x0,      y0, x1: x, y1 }) );

			//Find the boundary between where this item does and does not exist along the strip of x axis found above
			const y = this._binarySearch(y1, y0, y => this._isItemContainedIn(item, { x0: x-1, y0, x1: x, y1: y }) );

			return {
				selectedNode: this.viprWidget.domNode,
				xOffset: x,
				yOffset: y
			};
		}

		/*
		 * Perform a binary search for the boundary between a condition succeeding and failing
		 * @param {number} maxPass - the maximum value where the condition is known to pass
		 * @param {number} maxFail - the maximum value where the condition is known to fail
		 * @param {function} test  - condition
		 */
		_binarySearch(maxPass, maxFail, test) {
			while(Math.abs(maxPass - maxFail) > 1) {
				const candidate = Math.round((maxPass + maxFail) / 2);
				if(test(candidate)) {
					maxPass = candidate;
				} else {
					maxFail = candidate;
				}
			}
			return maxPass;
		}

		/*
		 * Determines whether the item exists inside a particular rectangle in the visualization
		 * @param {object} item
		 * @param {object} extents - rectangle
		 */
		_isItemContainedIn(item, { x0, y0, x1, y1 }) {
			return this._getVIDAItemsInRectangle({ x0, y0, x1, y1 }).some(foundItem => item.key === foundItem.key);
		}

	}

	return BinarySearchTupleLocator;
});
